/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import axios from '../../services/axios';
import Loading from '../Loading';

// ✅ Design System Components
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../styles/layout/Section';
import {
   CardGrid,
   MiniCard,
   MiniCardTitle,
   MiniCardValue,
} from '../../styles/layout/CardGrid';
import { FormGrid } from '../../styles/layout/FormGrid';

// =====================
// Wrapper com Scroll
// =====================

const AuditWrapper = styled.div`
   max-height: 70vh;
   overflow-y: auto;
   padding-right: 8px;

   /* Scrollbar customizada */
   &::-webkit-scrollbar {
      width: 8px;
   }

   &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme?.colors?.surface ?? '#f1f1f1'};
      border-radius: 10px;
   }

   &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme?.colors?.border ?? '#888'};
      border-radius: 10px;

      &:hover {
         background: ${({ theme }) => theme?.colors?.textMuted ?? '#555'};
      }
   }
`;

// =====================
// Field Component (caso não tenha)
// =====================

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;

   label {
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
   }

   input,
   select {
      padding: 10px 12px;
      border: 1px solid ${({ theme }) => theme?.colors?.border ?? '#ddd'};
      border-radius: 8px;
      font-size: 14px;
      background: ${({ theme }) => theme?.colors?.surface ?? 'white'};
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#333'};
      transition: border-color 0.2s;

      &:focus {
         outline: none;
         border-color: ${({ theme }) => theme?.colors?.primary ?? '#0197f6'};
      }
   }

   button {
      padding: 10px 16px;
      background: ${({ theme }) => theme?.colors?.primary ?? '#0197f6'};
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.2s;

      &:hover {
         background: ${({ theme }) => theme?.colors?.primaryDark ?? '#0177d6'};
      }

      &:disabled {
         background: #ccc;
         cursor: not-allowed;
      }
   }
`;

// =====================
// Timeline Components
// =====================

const Timeline = styled.div`
   position: relative;
   padding-left: 32px;
   min-height: 200px;

   &::before {
      content: '';
      position: absolute;
      left: 8px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: linear-gradient(
         to bottom,
         ${({ theme }) => theme?.colors?.border ?? '#e0e0e0'} 0%,
         ${({ theme }) => theme?.colors?.surface ?? '#f5f5f5'} 100%
      );
   }
`;

const TimelineItem = styled.div`
   position: relative;
   padding: 16px 0;
   padding-left: 24px;

   &::before {
      content: '';
      position: absolute;
      left: -24px;
      top: 24px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: ${(props) => {
         if (props.$action === 'created') return '#4caf50';
         if (props.$action === 'updated') return '#2196f3';
         if (props.$action === 'deleted') return '#f44336';
         return '#999';
      }};
      border: 3px solid ${({ theme }) => theme?.colors?.surface ?? 'white'};
      box-shadow: 0 0 0 2px
         ${(props) => {
            if (props.$action === 'created') return '#4caf50';
            if (props.$action === 'updated') return '#2196f3';
            if (props.$action === 'deleted') return '#f44336';
            return '#999';
         }};
   }
`;

const TimelineCard = styled.div`
   background: ${({ theme }) => theme?.colors?.surface ?? 'white'};
   border: 1px solid ${({ theme }) => theme?.colors?.border ?? '#e0e0e0'};
   border-radius: 12px;
   padding: 16px;
   box-shadow: ${({ theme }) =>
      theme?.shadows?.sm ?? '0 1px 3px rgba(0,0,0,0.05)'};
   transition: all 0.2s;

   &:hover {
      box-shadow: ${({ theme }) =>
         theme?.shadows?.md ?? '0 2px 8px rgba(0,0,0,0.1)'};
      transform: translateX(2px);
   }
`;

const TimelineHeader = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   margin-bottom: 12px;
   flex-wrap: wrap;
   gap: 8px;
`;

const ActionBadge = styled.span`
   display: inline-block;
   padding: 4px 12px;
   border-radius: 12px;
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.3px;
   background: ${(props) => {
      if (props.$action === 'created') return '#e8f5e9';
      if (props.$action === 'updated') return '#e3f2fd';
      if (props.$action === 'deleted') return '#ffebee';
      return '#f5f5f5';
   }};
   color: ${(props) => {
      if (props.$action === 'created') return '#2e7d32';
      if (props.$action === 'updated') return '#1565c0';
      if (props.$action === 'deleted') return '#c62828';
      return '#666';
   }};
`;

const TimelineDate = styled.span`
   font-size: 12px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#999'};
   font-weight: 500;
`;

const TimelineDescription = styled.p`
   margin: 8px 0;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#333'};
   font-size: 14px;
   line-height: 1.6;
   font-family: 'Courier New', monospace;
   background: ${({ theme }) => theme?.colors?.surfaceAlt ?? '#f9f9f9'};
   padding: 8px 12px;
   border-radius: 6px;
`;

const TimelineUser = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 13px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   margin-top: 8px;
   padding-top: 8px;
   border-top: 1px solid ${({ theme }) => theme?.colors?.border ?? '#f0f0f0'};

   &::before {
      content: '👤';
      font-size: 14px;
   }

   strong {
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#333'};
      font-weight: 600;
   }
`;

const EmptyStateContainer = styled.div`
   text-align: center;
   padding: 64px 24px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#999'};

   h4 {
      margin: 0 0 8px 0;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#666'};
      font-size: 18px;
   }

   p {
      margin: 0;
      font-size: 14px;
   }
`;

const Pagination = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   gap: 16px;
   padding: 16px;
   background: ${({ theme }) => theme?.colors?.surfaceAlt ?? '#f9f9f9'};
   border-radius: 8px;
   margin-top: 16px;

   button {
      padding: 8px 16px;
      background: ${({ theme }) => theme?.colors?.surface ?? 'white'};
      border: 1px solid ${({ theme }) => theme?.colors?.border ?? '#ddd'};
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.2s;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#333'};

      &:hover:not(:disabled) {
         background: ${({ theme }) => theme?.colors?.primarySoft ?? '#f5f5f5'};
         border-color: ${({ theme }) => theme?.colors?.primary ?? '#0197f6'};
         color: ${({ theme }) => theme?.colors?.primary ?? '#0197f6'};
      }

      &:disabled {
         opacity: 0.5;
         cursor: not-allowed;
      }
   }

   span {
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
      font-size: 14px;
      font-weight: 500;
   }
`;

// =====================
// Component
// =====================

export default function ProductAudit({ productId }) {
   const [isLoading, setIsLoading] = useState(false);
   const [stats, setStats] = useState(null);
   const [history, setHistory] = useState([]);
   const [pagination, setPagination] = useState({
      current_page: 1,
      total_pages: 1,
      total: 0,
   });

   const [filters, setFilters] = useState({
      action: '',
      startDate: '',
      endDate: '',
   });

   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 20;

   const fetchChangelog = useCallback(async (page = 1) => {
      try {
         setIsLoading(true);

         const params = new URLSearchParams({
            limit: itemsPerPage,
            offset: (page - 1) * itemsPerPage,
         });

         if (filters.action) params.append('action', filters.action);
         if (filters.startDate) params.append('startDate', filters.startDate);
         if (filters.endDate) params.append('endDate', filters.endDate);

         const response = await axios.get(
            `/products/${productId}/changelog?${params.toString()}`
         );

         const { data } = response.data;

         setStats(data.stats);
         setHistory(data.history);
         setPagination(data.pagination);
         setCurrentPage(page);
      } catch (err) {
         const errorMsg =
            err?.response?.data?.errors?.[0]?.message ||
            err?.response?.data?.message ||
            'Erro ao carregar histórico';
         toast.error(errorMsg);
      } finally {
         setIsLoading(false);
      }
   }, [productId, filters, itemsPerPage]);

   useEffect(() => {
      if (productId) {
         fetchChangelog(1);
      }
   }, [productId, fetchChangelog]);

   const handleApplyFilters = () => {
      fetchChangelog(1);
   };

   const handleClearFilters = () => {
      setFilters({
         action: '',
         startDate: '',
         endDate: '',
      });
      setTimeout(() => fetchChangelog(1), 0);
   };

   const formatDate = (dateString) => {
      if (!dateString) return '--';
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
         day: '2-digit',
         month: '2-digit',
         year: 'numeric',
         hour: '2-digit',
         minute: '2-digit',
      });
   };

   return (
      <AuditWrapper>
         <Loading isLoading={isLoading} />

         {/* Stats */}
         {stats && (
            <Section>
               <SectionHeader>
                  <SectionTitle>Estatísticas</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <CardGrid $span={12} $mdSpan={4} $lgSpan={4}>
                     <MiniCard style={{ borderLeft: '4px solid #4caf50' }}>
                        <MiniCardTitle>Criado em</MiniCardTitle>
                        <MiniCardValue style={{ fontSize: '16px' }}>
                           {formatDate(stats.created_at)}
                        </MiniCardValue>
                     </MiniCard>

                     <MiniCard style={{ borderLeft: '4px solid #ff9800' }}>
                        <MiniCardTitle>Última alteração</MiniCardTitle>
                        <MiniCardValue style={{ fontSize: '16px' }}>
                           {formatDate(stats.last_change_at)}
                        </MiniCardValue>
                     </MiniCard>

                     <MiniCard style={{ borderLeft: '4px solid #2196f3' }}>
                        <MiniCardTitle>Total de alterações</MiniCardTitle>
                        <MiniCardValue>{stats.total_changes}</MiniCardValue>
                     </MiniCard>
                  </CardGrid>
               </SectionBody>
            </Section>
         )}

         {/* Filters */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <FormGrid>
                  <Field className="col-3">
                     <label>Ação</label>
                     <select
                        value={filters.action}
                        onChange={(e) =>
                           setFilters((prev) => ({
                              ...prev,
                              action: e.target.value,
                           }))
                        }
                     >
                        <option value="">Todas</option>
                        <option value="created">Criação</option>
                        <option value="updated">Atualização</option>
                        <option value="deleted">Exclusão</option>
                     </select>
                  </Field>

                  <Field className="col-3">
                     <label>Data inicial</label>
                     <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) =>
                           setFilters((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                           }))
                        }
                     />
                  </Field>

                  <Field className="col-3">
                     <label>Data final</label>
                     <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) =>
                           setFilters((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                           }))
                        }
                     />
                  </Field>

                  <Field
                     className="col-3"
                     style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-end',
                     }}
                  >
                     <button type="button" onClick={handleApplyFilters}>
                        Filtrar
                     </button>
                     <button
                        type="button"
                        onClick={handleClearFilters}
                        style={{ background: '#999' }}
                     >
                        Limpar
                     </button>
                  </Field>
               </FormGrid>
            </SectionBody>
         </Section>

         {/* Timeline */}
         <Section>
            <SectionHeader>
               <SectionTitle>Histórico de Alterações</SectionTitle>
            </SectionHeader>
            <SectionBody>
               {history.length === 0 ? (
                  <EmptyStateContainer>
                     <h4>Nenhum histórico encontrado</h4>
                     <p>
                        Ainda não há alterações registradas para este produto.
                     </p>
                  </EmptyStateContainer>
               ) : (
                  <>
                     <Timeline>
                        {history.map((log) => (
                           <TimelineItem key={log.id} $action={log.action}>
                              <TimelineCard>
                                 <TimelineHeader>
                                    <ActionBadge $action={log.action}>
                                       {log.action}
                                    </ActionBadge>
                                    <TimelineDate>
                                       {formatDate(log.created_at)}
                                    </TimelineDate>
                                 </TimelineHeader>

                                 <TimelineDescription>
                                    {log.description}
                                 </TimelineDescription>

                                 {log.user && (
                                    <TimelineUser>
                                       <span>
                                          <strong>{log.user.name}</strong>
                                       </span>
                                       <span>({log.user.email})</span>
                                    </TimelineUser>
                                 )}
                              </TimelineCard>
                           </TimelineItem>
                        ))}
                     </Timeline>

                     {pagination.total_pages > 1 && (
                        <Pagination>
                           <button
                              type="button"
                              onClick={() => fetchChangelog(currentPage - 1)}
                              disabled={currentPage === 1}
                           >
                              ← Anterior
                           </button>

                           <span>
                              Página {currentPage} de {pagination.total_pages}
                           </span>

                           <button
                              type="button"
                              onClick={() => fetchChangelog(currentPage + 1)}
                              disabled={currentPage === pagination.total_pages}
                           >
                              Próxima →
                           </button>
                        </Pagination>
                     )}
                  </>
               )}
            </SectionBody>
         </Section>
      </AuditWrapper>
   );
}
